import {
  CollaborationAccessEnum,
  IQuoteRequestSchema,
} from '@monetize/types/app';
import { useFormContext } from 'react-hook-form';
import { MdSettings } from 'react-icons/md';
import { Link as ReactDomLink } from 'react-router-dom';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MLink,
  MPageLoader,
  MStatusIDBox,
  MText,
} from '../../../../components/Monetize';
import { EntityJsonViewerIcon } from '../../../../components/Monetize/EntityJsonViewerIcon';
import { QuoteBodyAccordion } from '../../../../components/Quotes/QuoteBodyAccordion';
import { QuoteCollaborationPopoverV2 } from '../../../../components/Quotes/QuoteCollaboration/QuoteCollaborationPopoverV2';
import { ROUTES } from '../../../../constants';
import { useQuoteContactsDrawer } from '../../Quote/components/contacts/quoteContactsDrawerContext';
import { useQuoteContext } from '../../Quote/quoteContext';
import { QuoteBasicContacts } from './QuoteBasicContacts';
import { QuoteBasicContract } from './QuoteBasicContract';
import { QuoteBasicDetails } from './QuoteBasicDetails';
import { QuotePriorContractLink } from './QuotePriorContractLink';

interface QuoteBasicProps {
  value?: string;
  onModifiedAccount?: () => void;
}

export const QuoteBasic = ({ value, onModifiedAccount }: QuoteBasicProps) => {
  const { quoteData, handleSubmitButtonWithoutDirtyCheck } = useQuoteContext();
  const { openContactsDrawer } = useQuoteContactsDrawer();

  const { quote } = quoteData;

  const { setValue } = useFormContext<IQuoteRequestSchema>();

  if (!quote) {
    return <MPageLoader />;
  }

  // A renewal has no contract of its own until it is processed
  const priorContractId = quote.contractId
    ? undefined
    : quote.previousContractId;

  return (
    <QuoteBodyAccordion value={value} title="Quote Setup">
      <MFlex flexDir={['column', 'column', 'column', 'row']}>
        <MBox flex="1">
          <MFlex
            borderBottomWidth="1px"
            borderColor="tGray.support"
            alignItems="center"
            justifyContent="space-between"
          >
            <MFlex my="2" alignItems="center">
              <MText mr={2} fontWeight="600" fontSize="md">
                {priorContractId ? 'Prior Contract' : 'Contract'}
              </MText>
              <MBox>
                {priorContractId ? (
                  <QuotePriorContractLink contractId={priorContractId} />
                ) : (
                  <MLink fontSize="sm" truncate maxWidth="200px" asChild>
                    <ReactDomLink
                      to={ROUTES.getQuoteContractEditRoute(
                        quote.id,
                        quote.contractId!,
                      )}
                      replace
                    >
                      {quote.contractId}
                    </ReactDomLink>
                  </MLink>
                )}
              </MBox>
            </MFlex>
          </MFlex>
          <QuoteBasicContract onModifiedAccount={onModifiedAccount} />
        </MBox>
        <MBox mx="6" display={['hidden', 'hidden', 'visible']}>
          <MBox w="1px" background="tGray.support" height="100%"></MBox>
        </MBox>
        <MBox flex="1">
          <MFlex
            borderBottomWidth="1px"
            borderColor="tGray.support"
            alignItems="center"
            justifyContent="space-between"
          >
            <MText my="2" fontWeight="600" fontSize="md">
              Contacts
            </MText>

            <MCustomIconButton
              btnSize={4}
              minW={6}
              minH={6}
              variant="icon"
              icon={MdSettings}
              iconColor="tIndigo.base"
              aria-label="Edit contacts"
              data-testid="contact-drawer-open-btn"
              onClick={() => openContactsDrawer()}
            />
          </MFlex>
          <QuoteBasicContacts />
        </MBox>
        <MBox mx="6" display={['hidden', 'hidden', 'visible']}>
          <MBox w="1px" background="tGray.support" height="100%"></MBox>
        </MBox>
        <MBox flex="1">
          <MFlex
            borderBottomWidth="1px"
            borderColor="tGray.support"
            alignItems="center"
            justifyContent="space-between"
          >
            <MFlex my="2" alignItems="center">
              <MText mr={2} fontWeight="600" fontSize="md">
                Details
              </MText>
              <MStatusIDBox
                id={quote?.id!}
                displayIcon={false}
                idCopyProps={{ fontSize: 'sm' }}
              />
              <EntityJsonViewerIcon json={quote} />
            </MFlex>

            <MBox mr="-1">
              <QuoteCollaborationPopoverV2
                quoteId={quote.id}
                quoteExpirationDate={quote.expirationDate}
                quoteStatus={quote.status}
                collaborationAccess={quote.collaborationAccess}
                accessLevel={quote.accessLevel}
                onCollaborationAccessChange={(
                  collaborationAccess: CollaborationAccessEnum,
                ) => {
                  setValue('collaborationAccess', collaborationAccess, {
                    shouldDirty: true,
                  });
                  handleSubmitButtonWithoutDirtyCheck();
                }}
              />
            </MBox>
          </MFlex>
          <QuoteBasicDetails />
        </MBox>
      </MFlex>
    </QuoteBodyAccordion>
  );
};
